import { useEffect, useState } from "react";
import { usePickup } from "/@/hooks/usePickup";
import {
  CreatePickup,
  STYLE,
  TranslationsAvailable,
  Pickup,
  Table,
  GET_PICKUP_AVAILABILITY,
} from "@jasper/shared";
import { Button, Modal } from "@mantine/core";
import { useLazyQuery } from "@apollo/client";
import { notifications } from "@mantine/notifications";
import { UseAuth } from "/@/contexts";

const PickupList = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [pickups, setPickups] = useState<Pickup[]>([]);
  const [pickupAvailables, setPickupAvailables] = useState([]);
  const [displayLoadMoreButton, setDisplayLoadMoreButton] =
    useState<boolean>(true);
  const [isCreatePickupModalOpened, setIsCreatePickupModalOpened] =
    useState<boolean>(false);
  const [selectedPickup, setSelectedPickup] = useState<Pickup>();
  const { fetchPickups, isLoading, editPickupById } = usePickup();

  const { user } = UseAuth();

  const PAGINATION_TAKE = 50;

  const init = async (selectedDate: Date) => {
    const req = await fetchPickups(selectedDate, PAGINATION_TAKE, 0);
    setPickups(req.data);
    setDisplayLoadMoreButton(req.data.length < req.totalCount);
  };

  const [getPickupAvailability] = useLazyQuery(GET_PICKUP_AVAILABILITY);

  useEffect(() => {
    init(selectedDate);
  }, [false]);

  if (isLoading) {
    return <>Loading...</>;
  }

  return (
    <>
      <Modal
        opened={isCreatePickupModalOpened}
        onClose={() => setIsCreatePickupModalOpened(false)}
        size="auto"
      >
        <CreatePickup
          onEdit={async (data: {
            clinicClosingTime: string;
            packageReadyTime: string;
            date: string;
            comment: string | null;
            trackingNumber?: string;
          }) => {
            if (!selectedPickup) {
              return null;
            }
            if (await editPickupById(selectedPickup.id, data, user.email)) {
              setIsCreatePickupModalOpened(false);
              await init(selectedDate);
            }
          }}
          orderId={selectedPickup?.order?.id}
          orderReference={selectedPickup?.order?.orderReference}
          lang={TranslationsAvailable.EN}
          pickupCarrier={selectedPickup?.order?.userGroup?.pickupCarrier}
          actualPickup={selectedPickup}
          pickupAvailabilities={pickupAvailables}
          connectedUserEmail={user.email}
        />
      </Modal>
      <input
        type="date"
        value={selectedDate.toISOString().split("T")[0]}
        style={{ margin: "1rem" }}
        onChange={async e => {
          if (!e.target.value) {
            return;
          }
          const calendarSelectedDate = new Date(e.target.value);
          const req = await fetchPickups(
            calendarSelectedDate,
            PAGINATION_TAKE,
            0
          );
          setPickups(req.data);
          setDisplayLoadMoreButton(req.data.length < req.totalCount);
          setSelectedDate(calendarSelectedDate);
        }}
      />
      {pickups.length === 0 ? (
        <div>
          There is no pickups scheduled for {selectedDate.toLocaleDateString()}
        </div>
      ) : (
        <Table
          columns={[
            "Doctor",
            "Order",
            "Carrier",
            "Clinic",
            "Clinic closing time",
            "Patient",
            "Status",
            "Comment",
            "Tracking number",
          ]}
          eventClick={pickups.map((pickup: Pickup) => {
            return async () => {
              notifications.show({
                title: "Loading..",
                color: "blue",
                message: "",
                loading: true,
                id: "loading-pickup-modal",
                autoClose: false,
              });
              setSelectedPickup(pickup);
              try {
                const req = await getPickupAvailability({
                  variables: {
                    orderId: pickup.order.id,
                  },
                });
                setPickupAvailables(req?.data?.getPickupAvailability ?? []);
              } catch (e) {
                console.error(e);
                notifications.hide("loading-pickup-modal");
                notifications.show({
                  title: "Error while trying to get pickup availabilities",
                  color: "red",
                  message: "",
                });
                return;
              }
              notifications.hide("loading-pickup-modal");
              setIsCreatePickupModalOpened(true);
            };
          })}
          rows={pickups.map((pickup: Pickup) => [
            `${pickup?.order?.user?.firstName ?? "N/A"} ${pickup?.order?.user?.lastName ?? "N/A"}`,
            pickup.order.orderReference,
            pickup?.order?.userGroup?.pickupCarrier ?? "N/A",
            `${pickup?.order?.userGroup?.clinicNumber} - ${pickup?.order?.userGroup?.name ?? "N/A"}`,
            pickup?.closingTime ?? "N/A",
            `${pickup?.order?.patient?.firstName ?? "N/A"} ${pickup?.order?.patient?.lastName ?? "N/A"}`,
            pickup?.status ?? "N/A",
            pickup?.comment ?? "N/A",
            pickup?.shipping?.trackingNumber ?? "N/A",
          ])}
        />
      )}
      {displayLoadMoreButton && (
        <div style={{ textAlign: "center", paddingTop: "2rem" }}>
          <Button
            style={{ backgroundColor: STYLE.primary }}
            onClick={async () => {
              const req = await fetchPickups(
                selectedDate,
                PAGINATION_TAKE,
                pickups.length
              );
              const newPickupList = [...pickups, ...req.data];
              setDisplayLoadMoreButton(newPickupList.length < req.totalCount);
              setPickups(newPickupList);
            }}
          >
            Load more
          </Button>
        </div>
      )}
    </>
  );
};

export default PickupList;
